import { useContext } from 'react';
import { AppContext } from '../window';
import { TranslationKeys } from './types';

/**
 * Translation hook for accessing and formatting translations
 * 
 * Usage:
 * const { t } = useTranslation();
 * 
 * Simple translation:
 * t('general.next') // Returns: "Next"
 * 
 * With variables:
 * t('errors.nickname_too_short', { minLength: 4 }) 
 * // Returns: "Nickname must be at least 4 characters long."
 * 
 * With nested HTML:
 * t('example.text', { boldPart: `<strong>${t('example.bold_text')}</strong>` })
 */
export const useTranslation = () => {
  const { texts } = useContext(AppContext);

  /**
   * Get translation by key path with optional variable replacement
   * 
   * @param keyPath - Dot-notation path to translation (e.g., 'general.next')
   * @param variables - Optional object with variables to replace in the translation
   * @returns Translated and formatted string
   */
  const t = (keyPath: string, variables?: Record<string, string | number>): string => {
    // Navigate through nested object using dot notation
    const keys = keyPath.split('.');
    let value: any = texts;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        // Translation not found - log error and return English fallback
        console.error(`Translation key not found: ${keyPath}`);
        
        // Try to get English fallback
        const enFallback = getEnglishFallback(keyPath);
        if (enFallback) {
          value = enFallback;
          break;
        }
        
        // If no fallback, return the key itself
        return keyPath;
      }
    }

    // Ensure we have a string value
    if (typeof value !== 'string') {
      console.error(`Translation value is not a string: ${keyPath}`);
      return keyPath;
    }

    // Replace variables if provided
    if (variables) {
      return replaceVariables(value, variables);
    }

    return value;
  };

  /**
   * Check if a translation key exists
   */
  const hasTranslation = (keyPath: string): boolean => {
    const keys = keyPath.split('.');
    let value: any = texts;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return false;
      }
    }

    return typeof value === 'string';
  };

  return { t, hasTranslation, texts };
};

/**
 * Replace variables in a translation string
 * Variables are defined as {variableName} in the translation
 * 
 * @param text - The translation text with variable placeholders
 * @param variables - Object with variable values
 * @returns Text with variables replaced
 */
function replaceVariables(text: string, variables: Record<string, string | number>): string {
  let result = text;
  
  Object.entries(variables).forEach(([key, value]) => {
    // Replace all occurrences of {key} with the value
    const placeholder = `{${key}}`;
    result = result.split(placeholder).join(String(value));
  });

  return result;
}

/**
 * Get English fallback for a translation key
 * This is used when a translation is missing in the current language
 * 
 * @param keyPath - Dot-notation path to translation
 * @returns English translation or null if not found
 */
function getEnglishFallback(keyPath: string): string | null {
  // In production, this would load from the English translation file
  // For now, we'll return null and rely on the key being returned
  // The actual English fallback will be handled by the backend
  return null;
}

/**
 * Helper function to get translation outside of React components
 * Note: This should only be used in special cases. Prefer useTranslation hook.
 * 
 * @param texts - The texts object from AppContext
 * @param keyPath - Dot-notation path to translation
 * @param variables - Optional variables to replace
 */
export function translate(
  texts: TranslationKeys,
  keyPath: string,
  variables?: Record<string, string | number>
): string {
  const keys = keyPath.split('.');
  let value: any = texts;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      console.error(`Translation key not found: ${keyPath}`);
      return keyPath;
    }
  }

  if (typeof value !== 'string') {
    console.error(`Translation value is not a string: ${keyPath}`);
    return keyPath;
  }

  if (variables) {
    return replaceVariables(value, variables);
  }

  return value;
}
